<script setup lang="ts">
import {defineModel, watch} from "vue";
import debounce from "lodash/debounce";
import { endsWithSeparator, stripNonNumeric } from "@/functions/numbers";
import InputLabel from "@/components/UI/InputLabel.vue";
import InputGroup from "@/components/UI/InputGroup.vue";

interface Props {
	label?: string;
	instructions?: string;
	unit?: string;
	name: string;
	id: string;
	invalid: boolean;
	inputmode?: "decimal" | "text" | "numeric" | "search" | "email" | "tel" | "url" | "none";
	hidden?: boolean;
	onBlur?: () => void;
	isLoading?: boolean;
	disabled?: boolean;
	readonly?: boolean;
	min?: number;
	max?: number;
	delay?: number;
	step?: number;
}

const model = defineModel();

const props = withDefaults(defineProps<Props>(), {
	inputmode: "decimal",
	delay: 200,
	step: 0.1,
});

const isNumerical = [ "decimal", "numeric" ].includes( props.inputmode );

// check if step is set to whole number (without decimal)
const isWholeNumber = (): boolean => {
	return props.step ? props.step - Math.floor(props.step) === 0 : false;
};

watch( model, ( newValue: string|number ) => {
	if (!isNumerical) {
		updateModel(newValue);
		return;
	}
	if ( isNumerical && !isWholeNumber() && ( endsWithSeparator( newValue ) || newValue === "" ) ) {
		return;
	}
	const numericNewValue = stripNonNumeric( newValue );
	updateModel(numericNewValue);
} );

const describedby = (): string => {
	const ids = [];
	if(props.invalid) {
		ids.unshift(`${props.id}-validation-message`);
	}
	return ids.join(' ');
};

// Alternative way of debouncing inputs. See https://michael-mckenna.com/debouncing-input-change-event-wait-until-user-stops-typing
const updateModel = debounce((newValue: string|number) => {
	model.value = newValue;
}, props.delay);

</script>

<template>
	<input-group v-show="!props.hidden" class="dimension-input">
		<input-label v-if="label" class="dimension-input__label" :for="props.id" :instructions="instructions">
			{{ label }}
		</input-label>
		<span class="dimension-input__wrap">
			<input
				ref="input"
				:class="{ 'dimension-input__field': true, 'dimension-input__field--invalid': props.invalid }"
				v-model="model"
				:aria-describedby="describedby()"
				:disabled="disabled"
				:id="props.id"
				:inputmode="props.inputmode"
				:name="props.name"
				:readonly="readonly"
				type="text"
				:min="props.min ?? null"
				:max="props.max ?? null"
				@blur="onBlur"
			/>
			<span
				v-if="props.unit && !isLoading"
				:class="{ 'dimension-input__unit': true, 'dimension-input__unit--invalid': props.invalid }"
			>{{ props.unit }}</span>
			<span v-if="isLoading === true" class="dimension-input__spinner">
				<i class="icon icon-spinner2"></i>
			</span>
		</span>
	</input-group>
</template>

<style scoped lang="scss">
.dimension-input__label {
	@include font-size(14px, 16px);
	display: block;
	margin-bottom: 10px;
	color: $brand-blue-dark;
}

.dimension-input__wrap {
	position: relative;
}

.dimension-input__field {
	width: 100%;
	padding: 2px 15px;
	border-color: $border-color-blue;
	border-radius: 4px;
	height: 44px;
	&--invalid {
		color: $error-color;
		border-color: $error-color;
		background-color: $error-color-lighter;
	}
}

.dimension-input__spinner,
.dimension-input__unit {
	@include font-size(14px, 16px);
	position: absolute;
	top: 50%;
	right: 8px;
	transform: translateY(-50%);
	color: $brand-blue-dark;
}

.dimension-input__unit {
	&--invalid {
		color: $error-color;
	}
}

input[type=number].dimension-input__field {
	padding-right: 4px;
}

input[type=number] + .dimension-input__unit,
input[type=number] + .dimension-input__spinner {
	right: 25px;
}

</style>
